import React from "react";
import { ArrowLeft, ArrowRight, X, BookOpen, Stethoscope } from "lucide-react";
import BodyDiagram, { MUSCLE_LABELS } from "../BodyDiagram";
import { AreasWorkedCards } from "../AreasWorkedCards";
import RegionalQuickSelect from "../RegionalQuickSelect";
import AnatomyInfoPanel from "../AnatomyInfoPanel";
import TreatmentMapPanel from "../TreatmentMapPanel";
import LayerToggles, { DEFAULT_LAYERS } from "../LayerToggles";
import DiagramLegend from "../DiagramLegend";
import ClinicalWorkspace from "../clinical/ClinicalWorkspace";
import ClinicalInsightsInline from "./ClinicalInsightsInline";
import AnatomyViewer from "../anatomy/AnatomyViewer";
import AnatomyViewerV2 from "../anatomy/AnatomyViewerV2";
import { api } from "../../lib/api";
import { toast } from "sonner";

const LEARNING_KEY = "posturra.learningMode";

export default function DiagramSection({
  workedAreas,
  setWorkedAreas,
  analysis,
  setPhase,
  // Iter H — Treatment Map context
  clientId,
  client, // optional — passed by NewSession; drives sex/name in workspace header
  sessionId, // current draft session id — needed to scope clinical intelligence
  sessionType,
  intakeNotes,
  previousSession,
  progressSinceLast,
  clinicalSnapshot,
  treatmentMap,
  setTreatmentMap,
  // Iter O — long-press a muscle to open the mic capture
  onMuscleLongPress,
  // Iter R — live captures saved this session + workspace-level mic trigger
  clinicalCapture = [],
  onWorkspaceMicTrigger,
  micActive = false,
}) {
  const [infoMuscleId, setInfoMuscleId] = React.useState(null);
  // Iter AH — V2 anatomy viewer drives its own selection (separate from the
  // legacy AnatomyInfoPanel slide-out which is still used by the classic body
  // diagram + Selected chips info buttons).
  const [v2SelectedMuscleId, setV2SelectedMuscleId] = React.useState(null);
  const [learningMode, setLearningMode] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(LEARNING_KEY) === "1";
  });
  const [layers, setLayers] = React.useState(DEFAULT_LAYERS);
  const [generating, setGenerating] = React.useState(false);
  const [classicMode, setClassicMode] = React.useState(false);
  const autoTriedRef = React.useRef(false);

  // ── Iter R: real Clinical Intelligence (previous findings, new-since-last, treatment logic) ──
  const [intel, setIntel] = React.useState(null);
  React.useEffect(() => {
    if (!clientId) return;
    const params = sessionId ? `?current_session_id=${encodeURIComponent(sessionId)}` : "";
    api.get(`/clients/${clientId}/clinical-intelligence${params}`)
      .then((r) => setIntel(r.data))
      .catch(() => setIntel(null));
  }, [clientId, sessionId, clinicalCapture.length]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LEARNING_KEY, learningMode ? "1" : "0");
  }, [learningMode]);

  // Auto-open info panel only in Learning Mode. In Clinical Mode tapping a muscle
  // just toggles selection (faster charting).
  const handleMuscleClick = (id) => {
    if (learningMode) setInfoMuscleId(id);
  };

  const toggleSelectFromPanel = (id) => {
    const next = workedAreas.includes(id) ? workedAreas.filter((x) => x !== id) : [...workedAreas, id];
    setWorkedAreas(next);
  };

  const generateMap = async () => {
    if (!clientId) return;
    setGenerating(true);
    try {
      const r = await api.post("/treatment-map/suggest", {
        client_id: clientId,
        session_type: sessionType,
        intake_notes: intakeNotes || null,
        ai_analysis: analysis,
        previous_session: previousSession,
        progress_since_last: progressSinceLast,
        clinical_snapshot: clinicalSnapshot,
      });
      setTreatmentMap?.(r.data);
      toast.success("Treatment map ready");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        toast.error(e.response?.data?.detail || "AI temporarily unavailable.");
      } else if (status === 402) {
        toast.error(e.response?.data?.detail || "AI quota reached.");
      } else {
        toast.error("Could not generate treatment map.");
      }
    } finally {
      setGenerating(false);
    }
  };

  // The AI treatment plan is fully automatic: fires once per Diagram mount when
  // no map exists yet. Regenerate stays available for manual refreshes.
  React.useEffect(() => {
    if (autoTriedRef.current) return;
    if (treatmentMap) return;
    if (generating) return;
    if (!clientId) return;
    autoTriedRef.current = true;
    toast.message("Auto-generating treatment plan…", { duration: 2500 });
    generateMap();
  }, [clientId, treatmentMap]);

  const acceptMap = () => {
    if (!treatmentMap) return;
    const all = [
      ...(treatmentMap.primary || []),
      ...(treatmentMap.secondary || []),
      ...(treatmentMap.related || []),
    ];
    const merged = new Set(workedAreas);
    all.forEach((m) => merged.add(m));
    setWorkedAreas(Array.from(merged));
  };

  const removeFromMap = (id) => {
    if (!treatmentMap) return;
    setTreatmentMap?.({
      ...treatmentMap,
      primary: (treatmentMap.primary || []).filter((m) => m !== id),
      secondary: (treatmentMap.secondary || []).filter((m) => m !== id),
      related: (treatmentMap.related || []).filter((m) => m !== id),
      // Iter AI — therapist edited the AI-generated plan; mark edited so the
      // badge can switch to "AI-generated · edited".
      edited_at: new Date().toISOString(),
    });
  };

  const showMap = layers.treatmentMap;
  const primary = showMap ? (treatmentMap?.primary || []) : [];
  const secondary = showMap ? (treatmentMap?.secondary || []) : [];
  const related = showMap ? (treatmentMap?.related || []) : [];
  const aiFocus = analysis?.focus_muscles || [];

  // ── Build workspace props from real session data ────────────────────────
  const workspaceTreatmentMap = React.useMemo(() => ({
    primary:   treatmentMap?.primary   || [],
    secondary: treatmentMap?.secondary || [],
    related:   treatmentMap?.related   || [],
  }), [treatmentMap]);

  const workspaceSteps = React.useMemo(() => {
    if (!treatmentMap) return [];
    const primaryIds = treatmentMap.primary || [];
    const secondaryIds = treatmentMap.secondary || [];
    const relatedIds = treatmentMap.related || [];
    // level/color follows the priority bucket only — never the sequence
    const levelOf = (id) =>
      primaryIds.includes(id) ? "primary"
      : secondaryIds.includes(id) ? "secondary"
      : relatedIds.includes(id) ? "related"
      : null;
    const META = {
      primary:   { time: "6 min", goal: "Primary release" },
      secondary: { time: "5 min", goal: "Address compensation" },
      related:   { time: "4 min", goal: "Settle related pattern" },
    };
    const makeStep = (id, num) => {
      const level = levelOf(id);
      const g = treatmentMap.guidance?.[id] || null;
      return {
        num, key: id, label: MUSCLE_LABELS[id] || id,
        time: META[level].time, goal: META[level].goal, level,
        intervention: g?.intervention_label || null,
        positionWhy: g?.position_uncommon ? g.position_why : null,
        positionAlt: g?.position_uncommon ? (g.position_alternatives_label || null) : null,
      };
    };

    let out = [];
    const seen = new Set();
    const POSITION_LABELS = { supine: "Supine", prone: "Prone", "side-lying": "Side-Lying", seated: "Seated" };
    // 1. Preferred: number by the AI's clinical hands-on order (workflow), color by priority
    if (Array.isArray(treatmentMap.workflow)) {
      treatmentMap.workflow.forEach((phase) => {
        const posLabel = POSITION_LABELS[phase?.position] || null;
        (phase?.muscles || []).forEach((id) => {
          if (seen.has(id)) return;          // dedupe muscles that appear in >1 phase
          if (!levelOf(id)) return;          // skip ids not in any priority bucket
          seen.add(id);
          out.push({ ...makeStep(id, out.length + 1), position: posLabel });
        });
      });
    }
    // 2. Fallback: workflow missing/empty/malformed → priority concatenation (previous behavior)
    if (out.length === 0) {
      seen.clear();
      [...primaryIds, ...secondaryIds, ...relatedIds].forEach((id) => {
        if (seen.has(id) || !levelOf(id)) return;
        seen.add(id);
        out.push(makeStep(id, out.length + 1));
      });
    }
    if (out.length > 0) {
      out.push({ num: out.length + 1, key: "_reassess", label: "Reassess & integrate", time: "5 min", goal: "ROM, posture, symptoms", level: "complete" });
    }
    return out;
  }, [treatmentMap]);

  const workspaceFindingsLibrary = React.useMemo(() => {
    const lib = {};
    [
      ...((treatmentMap?.primary)   || []).map((id) => ({ id, level: "primary"   })),
      ...((treatmentMap?.secondary) || []).map((id) => ({ id, level: "secondary" })),
      ...((treatmentMap?.related)   || []).map((id) => ({ id, level: "related"   })),
    ].forEach(({ id, level }) => {
      lib[id] = {
        label: MUSCLE_LABELS[id] || id,
        level,
        why: treatmentMap?.rationale ? `${treatmentMap.rationale}` : "Flagged by AI treatment map for this session.",
        sequence: [],
        history: [],
      };
    });
    return lib;
  }, [treatmentMap]);

  const onWorkspaceAddToPlan = React.useCallback((id) => {
    if (!workedAreas.includes(id)) {
      setWorkedAreas([...workedAreas, id]);
      toast.success(`${MUSCLE_LABELS[id] || id} added to plan`);
    }
  }, [workedAreas, setWorkedAreas]);

  const onWorkspaceAddressed = React.useCallback((id) => {
    if (id && id !== "_reassess" && !workedAreas.includes(id)) {
      setWorkedAreas([...workedAreas, id]);
    }
  }, [workedAreas, setWorkedAreas]);

  return (
    <div data-testid="diagram-phase">
      <div className="label-overline mb-2">Step 3 — Treatment map & body diagram</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">
        Plan and mark what you worked on
      </h1>
      <p className="text-ink-700 mb-6 max-w-2xl">
        Generate an AI treatment map prioritized by today&apos;s findings, then refine on the diagram.
        <span className="text-ink-500"> Areas turn </span>
        <span className="px-2 py-0.5 rounded bg-terracotta-500 text-white text-xs">terracotta</span>
        <span className="text-ink-500"> once you mark them as worked.</span>
      </p>

      {/* Clinical vs Learning Mode + Layer Toggles */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex rounded-full border border-paper-200 p-1 bg-white" data-testid="diagram-mode-toggle">
          <button
            type="button"
            onClick={() => setLearningMode(false)}
            data-testid="mode-clinical"
            className={`min-h-[40px] px-4 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition ${
              !learningMode ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100"
            }`}
          >
            <Stethoscope className="w-3.5 h-3.5" /> Clinical
          </button>
          <button
            type="button"
            onClick={() => setLearningMode(true)}
            data-testid="mode-learning"
            className={`min-h-[40px] px-4 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition ${
              learningMode ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100"
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" /> Learning
          </button>
        </div>
        {/* Iter AH — multi-toggle architecture (Labels/Trigger/Referral/Skeletal/Clinical/AI) replaced by the 3-tab AnatomyViewerV2 below.
            LayerToggles is now hidden under an Advanced disclosure for power users who still need the legacy overlays. */}
        <details className="ml-auto text-xs text-ink-500" data-testid="legacy-layer-toggle-advanced">
          <summary className="cursor-pointer hover:text-ink-700 select-none">Advanced layers</summary>
          <div className="mt-3"><LayerToggles value={layers} onChange={setLayers} /></div>
        </details>
      </div>

      {/* ───────── PRIMARY SURFACE — Clinician-first Anatomy Viewer (Iter AH) ───────── */}
      <div className="mb-6 -mx-1 sm:mx-0" data-testid="anatomy-primary-surface">
        <AnatomyViewerV2
          clientId={clientId}
          sessionId={sessionId}
          currentSessionWorked={workedAreas}
          previousSessionWorked={previousSession?.worked_areas || []}
          selectedMuscleId={v2SelectedMuscleId}
          onMuscleSelect={(id) => setV2SelectedMuscleId(id)}
          onAddToWorked={(id) => {
            if (!workedAreas.includes(id)) {
              setWorkedAreas([...workedAreas, id]);
              toast.success(`${MUSCLE_LABELS[id] || id} added to today's work`);
            }
          }}
          onRemoveFromWorked={(id) => {
            setWorkedAreas(workedAreas.filter((x) => x !== id));
          }}
          sex={client?.sex === "female" ? "f" : "m"}
          defaultView="anterior"
          patientName={client?.name || "Client"}
          patientMeta={client?.session_count != null ? `Session ${client.session_count + 1}` : null}
          testIdPrefix="diagram-anatomy"
        />
      </div>

      {/* ───────── Clinical anatomy overlay (Skeletal / Trigger points / Referral) — LEGACY, hidden by default ───────── */}
      {(layers.skeletal || layers.trigger || layers.referral) && (
        <div className="mb-6 bg-white border border-paper-200 rounded-3xl p-4 sm:p-6" data-testid="clinical-overlay-panel">
          <AnatomyViewer
            sex={client?.sex === "female" ? "f" : "m"}
            defaultView="posterior"
            activeTab={
              layers.skeletal ? "landmarks"
              : layers.trigger ? "triggerPoints"
              : "referral"
            }
            showTabStrip={false}
            testIdPrefix="diagram-overlay"
          />
        </div>
      )}

      {/* AI Treatment Map generator (compact — workspace surfaces results above) */}
      {layers.treatmentMap && (
        <TreatmentMapPanel
          map={treatmentMap}
          workedAreas={workedAreas}
          onGenerate={generateMap}
          onAccept={acceptMap}
          onMuscleClick={(id) => setInfoMuscleId(id)}
          onRemoveMuscle={removeFromMap}
          generating={generating}
        />
      )}

      {/* ───────── PRIMARY SURFACE — Clinical Workspace ───────── */}
      {workspaceSteps.length > 0 && (
        <div className="rounded-3xl bg-slate-900 p-4 sm:p-6 mb-6 -mx-1 sm:mx-0" data-testid="clinical-workspace-section">
          <ClinicalWorkspace
            client={{ name: client?.name || "Client", sex: client?.sex || "unspecified" }}
            sessionMeta={{ num: client?.session_count != null ? client.session_count + 1 : null, totalSessions: null, date: null }}
            stats={intel?.stats || {}}
            treatmentMap={workspaceTreatmentMap}
            treatmentSteps={workspaceSteps}
            treatmentRationale={treatmentMap?.rationale || ""}
            treatmentConfidence={treatmentMap?.confidence || 0.8}
            treatmentBasis={treatmentMap?.basis || "Generated from this session's intake + posture analysis"}
            expectedResult={treatmentMap?.expected_outcome || "Targeted release of the primary chain before posterior reset."}
            previousFindings={intel?.previous_findings || []}
            newSinceLastVisit={intel?.new_since_last_visit || null}
            treatmentLogic={intel?.treatment_logic || null}
            findingsLibrary={workspaceFindingsLibrary}
            learningLibrary={{}}
            clientPlainSteps={[]}
            progressPct={0}
            recentCaptures={[...clinicalCapture].reverse()}
            onStartPlan={() => toast.info("Treatment plan started")}
            onMuscleAddressed={onWorkspaceAddressed}
            onMuscleAddToPlan={onWorkspaceAddToPlan}
            onCaptureClick={onWorkspaceMicTrigger}
            captureActive={micActive}
            onMuscleLongPress={onMuscleLongPress}
          />
        </div>
      )}

      {/* ───────── Inline Clinical Insights (Iter AB) ───────── */}
      {(workedAreas?.length > 0 || (treatmentMap?.primary || []).length > 0) && (
        <ClinicalInsightsInline
          clientId={clientId}
          sessionId={sessionId}
          workedAreas={workedAreas}
          treatmentMap={treatmentMap}
        />
      )}

      {/* ───────── LEGACY controls — region chips, diagram, label list ───────── */}
      <details className="mb-6 group" data-testid="classic-mode-toggle" open={classicMode} onToggle={(e) => setClassicMode(e.currentTarget.open)}>
        <summary className="cursor-pointer text-sm text-ink-600 hover:text-ink-900 inline-flex items-center gap-1.5 select-none">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-paper-300 group-open:bg-sage-500" />
          {classicMode ? "Hide classic body diagram" : "Show classic body diagram (region chips + full muscle picker)"}
        </summary>
        <div className="mt-4 space-y-6">
          {/* Regional quick select */}
          <div className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6">
            <RegionalQuickSelect selected={workedAreas} onChange={setWorkedAreas} />
          </div>

          {/* Body region cards — universal realistic anatomy picker */}
          {layers.muscles && (
            <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
              <AreasWorkedCards
                workedAreas={workedAreas}
                readOnly={false}
                onChange={setWorkedAreas}
                diagramProps={{
                  aiFocus,
                  primaryFocus: primary,
                  secondaryFocus: secondary,
                  relatedFocus: related,
                  onMuscleClick: handleMuscleClick,
                  onMuscleLongPress,
                  defaultLayout: "large",
                  labelsOn: layers.labels,
                  onLabelsToggle: (v) => setLayers({ ...layers, labels: v }),
                  hideInternalControls: true,
                }}
              />
            </div>
          )}

          {/* Legend */}
          <DiagramLegend />
        </div>
      </details>

      {/* Selected chips */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-10">
        <div className="flex items-center justify-between mb-3">
          <div className="label-overline">Selected ({workedAreas.length})</div>
          {workedAreas.length > 0 && (
            <button
              onClick={() => setWorkedAreas([])}
              className="text-xs text-ink-500 hover:text-ink-900"
              data-testid="clear-areas-btn"
            >
              Clear all
            </button>
          )}
        </div>
        {workedAreas.length === 0 ? (
          <div className="text-sm text-ink-500">
            Tap any muscle group on the diagram, accept the treatment map, or use the region chips above.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {workedAreas.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-terracotta-500/10 text-terracotta-600 text-sm"
                data-testid={`selected-${m}`}
              >
                <button
                  type="button"
                  onClick={() => setInfoMuscleId(m)}
                  className="hover:underline"
                  data-testid={`selected-${m}-info`}
                  aria-label={`Show info for ${MUSCLE_LABELS[m] || m}`}
                >
                  {MUSCLE_LABELS[m] || m}
                </button>
                <button
                  type="button"
                  onClick={() => setWorkedAreas(workedAreas.filter((x) => x !== m))}
                  aria-label={`Remove ${MUSCLE_LABELS[m] || m}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setPhase("analysis")}
          className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={() => setPhase("soap")}
          className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2"
          data-testid="to-soap-btn"
        >
          SOAP notes <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Slide-out info panel */}
      <AnatomyInfoPanel
        muscleId={infoMuscleId}
        selected={workedAreas}
        onClose={() => setInfoMuscleId(null)}
        onToggleSelect={toggleSelectFromPanel}
        learningMode={learningMode}
        onLearningMode={setLearningMode}
      />
    </div>
  );
}
